import {
  FunctionComponent,
  useMemo,
  useState,
} from "react";
import { FacetBase } from "../../models/facet-base/facet-base";
import { CheckIcon } from "@radix-ui/react-icons";
import * as Checkbox from "@radix-ui/react-checkbox";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../system/redux/hooks/redux-store-hooks";

import { selectVisibleTextFacetValue } from "../../redux/selectors/select-visible-text-facet-value";
import { selectSelectedFacets } from "../../redux/selectors/select-selected-facets";
import { setFacetValue } from "../../redux/slices/facet-slice";
import { isFacetSelected } from "../../functions/filtering/is-facet-selected";

interface FacetProps {
  drhFacet: FacetBase;
}

const TextFacet: FunctionComponent<FacetProps> = ({ drhFacet }) => {
  const dispatch = useAppDispatch();
  
  const [visibleCount, setVisibleCount] = useState(10);

  const rawVisibleFacets = useAppSelector(
    selectVisibleTextFacetValue(drhFacet.fieldName)
  );

  const visibleFacets = useMemo(() => {
    if (!rawVisibleFacets) return [];
    return [...new Set(rawVisibleFacets.map(v => v.split(";")[0].trim()))];
  }, [rawVisibleFacets]);

  const selectedFacetMap = useAppSelector(selectSelectedFacets);

  const selectedValues = useMemo(() => {
    const facetValue = selectedFacetMap[drhFacet.fieldName];
    if (Array.isArray(facetValue) && facetValue.every(v => typeof v === 'string')) {
      return facetValue as string[];
    }
    return [];
  }, [selectedFacetMap, drhFacet.fieldName]);

  const getId = (value: string) => value.replaceAll(" ", "-");

  const handleFacetSelection = (value: string) => {
    dispatch(
      setFacetValue({
        facet: drhFacet,
        facetValues: [value],
      })
    );
  };

  const handleSeeMore = () => {
    setVisibleCount(prev => prev + 10);
  };

  const handleSeeLess = () => {
    setVisibleCount(10);
  };


  const displayFacets = useMemo(() => {
    if (!visibleFacets.length) return [];
    

    const selectedItems = visibleFacets.filter(v => selectedValues.includes(v));
    const unselectedItems = visibleFacets.filter(v => !selectedValues.includes(v));
    

    const paginatedUnselected = unselectedItems.slice(0, Math.max(0, visibleCount - selectedItems.length));
    
    return [...selectedItems, ...paginatedUnselected];
  }, [visibleFacets, selectedValues, visibleCount]);


  const hasMoreItems = useMemo(() => {
    const selectedItems = visibleFacets.filter(v => selectedValues.includes(v));
    const unselectedItems = visibleFacets.filter(v => !selectedValues.includes(v));
    return displayFacets.length < selectedItems.length + unselectedItems.length;
  }, [visibleFacets, selectedValues, displayFacets.length]);


  const isExpanded = visibleCount > 10;

  if (!visibleFacets.length) {
    return null;
  }

  return (
    <div className="facet">
      <div className="coveo-facet-header">
        <div className="coveo-facet-header-title-section">
          <div className="coveo-facet-header-title" role="heading" aria-level={2}>
            {drhFacet.title}
          </div>
        </div>

        <ul className="coveo-facet-values">
          {displayFacets.map((value) => (
            <li key={value}>
              <Checkbox.Root
                className="CheckboxRoot"
                id={getId(value)}
                value={value}
                name={value}
                checked={isFacetSelected(selectedFacetMap, drhFacet, [value])}
                onClick={() => handleFacetSelection(value)}
              >
                <Checkbox.Indicator className="CheckboxIndicator">
                  <CheckIcon />
                </Checkbox.Indicator>
              </Checkbox.Root>
              <label htmlFor={getId(value)}>{value}</label> 
            </li>
          ))}
        </ul>

        
        {(hasMoreItems || isExpanded) && (
          <div className="facet-pagination-buttons">
            {hasMoreItems && (
              <button 
                className="see-more-button-amenities" 
                onClick={handleSeeMore}
                type="button"
              >
                See more
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextFacet;